import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface ParsedDate {
  y: number;
  /** 0-based month */
  m: number;
  d: number;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function toYMD(y: number, m: number, d: number): string {
  return `${y}-${pad2(m + 1)}-${pad2(d)}`;
}

function parseYMD(value: string): ParsedDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) };
}

function todayYMD(): string {
  const t = new Date();
  return toYMD(t.getFullYear(), t.getMonth(), t.getDate());
}

export interface CalendarProps {
  /** Selected date as a zero-padded YYYY-MM-DD string (the app's API format). */
  value?: string;
  onSelect?: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Single month calendar grid. Manages its own visible month so it can be used
 * inside a popover (it mounts fresh whenever the popover opens). Values are
 * always emitted as zero-padded YYYY-MM-DD strings, exactly matching the
 * application's existing date format, validation and API contract.
 */
export function Calendar({ value, onSelect, min, max, disabled, className }: CalendarProps) {
  const selected = parseYMD(value || '');
  const today = todayYMD();

  const [view, setView] = useState(() => {
    const base = selected ? new Date(selected.y, selected.m, 1) : new Date();
    return { y: base.getFullYear(), m: base.getMonth() };
  });

  const firstWeekday = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const rows = Math.ceil((firstWeekday + daysInMonth) / 7);

  const minDate = parseYMD(min || '');
  const maxDate = parseYMD(max || '');

  const move = (delta: number) => {
    setView(({ y, m }) => {
      const next = new Date(y, m + delta, 1);
      return { y: next.getFullYear(), m: next.getMonth() };
    });
  };

  const atStart = minDate ? view.y === minDate.y && view.m === minDate.m : false;
  const atEnd = maxDate ? view.y === maxDate.y && view.m === maxDate.m : false;

  const inBounds = (day: number): boolean => {
    const ymd = toYMD(view.y, view.m, day);
    if (minDate && ymd < toYMD(minDate.y, minDate.m, minDate.d)) return false;
    if (maxDate && ymd > toYMD(maxDate.y, maxDate.m, maxDate.d)) return false;
    return true;
  };

  const handleDay = (day: number) => {
    onSelect?.(toYMD(view.y, view.m, day));
  };

  const cells = Array.from({ length: rows * 7 }, (_, index) => {
    const day = index - firstWeekday + 1;
    if (day < 1 || day > daysInMonth) return <div key={index} aria-hidden="true" />;

    const ymd = toYMD(view.y, view.m, day);
    const isSelected = !!selected && selected.y === view.y && selected.m === view.m && selected.d === day;
    const isToday = ymd === today;
    const cantPick = !!disabled || !inBounds(day);

    return (
      <button
        key={index}
        type="button"
        disabled={cantPick}
        aria-label={`${day} ${MONTH_NAMES[view.m]} ${view.y}`}
        aria-current={isSelected ? 'date' : undefined}
        onClick={() => handleDay(day)}
        className={cn(
          'flex h-8 w-full items-center justify-center rounded-md text-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          isSelected ? 'bg-brand-500 font-semibold text-white' : 'text-primary hover:bg-hover',
          isToday && !isSelected && 'ring-1 ring-inset ring-brand-500/60',
          cantPick && 'cursor-not-allowed text-muted/50 hover:bg-transparent'
        )}
      >
        {day}
      </button>
    );
  });

  return (
    <div className={cn('w-full select-none', className)}>
      <div className="flex items-center justify-between px-0.5 pb-2">
        <button
          type="button"
          aria-label="Previous month"
          disabled={!!disabled || atStart}
          onClick={() => move(-1)}
          className="flex h-8 w-8 items-center justify-center rounded-md text-secondary hover:bg-hover hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-40"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-sm font-semibold text-primary" aria-live="polite">
          {MONTH_NAMES[view.m]} {view.y}
        </div>
        <button
          type="button"
          aria-label="Next month"
          disabled={!!disabled || atEnd}
          onClick={() => move(1)}
          className="flex h-8 w-8 items-center justify-center rounded-md text-secondary hover:bg-hover hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-40"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center" role="grid" aria-label="Calendar">
        {WEEKDAYS.map((w) => (
          <div key={w} className="pb-1 text-xs font-medium text-secondary">
            {w}
          </div>
        ))}
        {cells}
      </div>
    </div>
  );
}